import type RootStore from "stores/RootStore";
import { RouteType } from "types/RouteType";
import type ClientAccessStore from "./ClientAccessStore";

/**
 * Administration → Client Access — the staff ↔ client matrix the
 * client-assignment gate reads (see ClientAccessStore for the rules).
 *
 * Dialogs are route-registered (the house pattern — see
 * views/admin/ApiKeys/apiKeys.Route.ts) so they get the router's chrome and
 * full-screen on phones.
 */

const storeOf = (rootStore: RootStore) =>
  rootStore.clientAccessStore as unknown as ClientAccessStore;

/**
 * A failed write THROWS: the dialog framework keeps a dialog open when its
 * onSubmit rejects and shows the message in the footer, so the error and the
 * form survive. Resolving closes it.
 */
const submitOrThrow = async (
  rootStore: RootStore,
  submit: (store: ClientAccessStore) => Promise<boolean>,
  errorOf: (store: ClientAccessStore) => string | null,
  fallback: string,
) => {
  const store = storeOf(rootStore);
  if (!(await submit(store))) throw new Error(errorOf(store) || fallback);
};

const clientAccessRoute: RouteType = {
  name: "Client Access",
  title: "Client Access",
  path: "/client-access",
  layout: "/app",
  viewportCard: {
    header: {
      enableBackButton: true,
      title: "Client Access",
      // false: this page loads from its Body, not an onEnter, so the plate's
      // refresh (which re-runs onEnter) would do nothing. The header renders
      // the working Refresh button.
      enableRefreshButton: false,
      color: "primary",
      component: () => import("./ClientAccess.Header"),
    },
    body: {
      component: () => import("./ClientAccess.Body"),
      grayBackground: false,
    },
  },
  group: "Admin",
  searchAliases: [
    "client assignments",
    "who can see which client",
    "assignment gate",
  ],
  requiresAuth: true,
  requiresAdmin: true,
  requiresPermission: false,
  enableAutoFind: false,
  dialogs: [
    {
      name: "GrantClientAccessDialog",
      title: "Grant client access",
      component: () => import("./dialogs/GrantClientAccessDialog.Body"),
      cancelButton: true,
      maxWidth: "sm",
      height: 420,
      submitButtonText: "Grant",
      onSubmit: async ({ rootStore }: { rootStore: RootStore }) =>
        submitOrThrow(
          rootStore,
          (store) => store.submitGrant(),
          (store) => store.grantError,
          "Could not grant access",
        ),
      onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) =>
        storeOf(rootStore).canSubmitGrant,
    },
    {
      name: "RemoveClientAccessDialog",
      title: "Remove client access",
      component: () => import("./dialogs/RemoveClientAccessDialog.Body"),
      cancelButton: true,
      maxWidth: "xs",
      height: 300,
      submitButtonText: "Remove",
      onSubmit: async ({ rootStore }: { rootStore: RootStore }) =>
        submitOrThrow(
          rootStore,
          (store) => store.executeRemove(),
          (store) => store.removeError,
          "Could not remove the assignment",
        ),
      onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
        const store = storeOf(rootStore);
        return !!store.removeTarget && !store.removing;
      },
      onClose: ({ rootStore }: { rootStore: RootStore }) =>
        storeOf(rootStore).cancelRemove(),
    },
  ],
};

export default clientAccessRoute;
